"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { ArrowUpRight } from "lucide-react";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

const DESTINATIONS = [
  {
    title: "Colombo",
    location: "Western Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/colombo.jpg`,
    route: "/travel-guides/western",
  },
  {
    title: "Kandy",
    location: "Central Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/kandy.jpg`,
    route: "/travel-guides/central",
  },
  {
    title: "Ella",
    location: "Uva Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/ella.jpg`,
    route: "/travel-guides/uva",
  },
  {
    title: "Sigiriya",
    location: "Central Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/sigiriya.jpg`,
    route: "/travel-guides/central",
  },
  {
    title: "Galle",
    location: "Southern Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/galle.jpg`,
    route: "/travel-guides/southern",
  },
  {
    title: "Nuwara Eliya",
    location: "Central Province",
    image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/nuwaraeliya.jpg`,
    route: "/travel-guides/central",
  },
];

export default function TopDestinations() {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const duration = 5000; // auto slide every 5s
  const router = useRouter();

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const itemsPerSlide = isMobile ? 1 : 4; // 1 on mobile, 4 on desktop
  const totalSlides = Math.ceil(DESTINATIONS.length / itemsPerSlide);

  const next = useCallback(
    () => setIndex((prev) => (prev + 1) % totalSlides),
    [totalSlides],
  );
  const prev = () => setIndex((prev) => (prev - 1 + totalSlides) % totalSlides);

  const goToSlide = (slideIndex: number) => {
    setIndex(slideIndex);
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(next, duration);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [index, next]);

  // Pause auto-slide on hover/touch
  const pauseAutoSlide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const resumeAutoSlide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(next, duration);
  };

  return (
    <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-0">
          Top Searched Destinations
        </h2>
        <button
          onClick={() => router.push("/travel-guides")}
          className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors inline-flex items-center"
        >
          View All Destinations
          <svg
            className="w-4 h-4 ml-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Carousel Wrapper */}
      <div
        className="relative overflow-hidden"
        onMouseEnter={pauseAutoSlide}
        onMouseLeave={resumeAutoSlide}
        onTouchStart={pauseAutoSlide}
        onTouchEnd={resumeAutoSlide}
      >
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {Array.from({ length: totalSlides }).map((_, slideIdx) => (
            <div key={slideIdx} className="flex min-w-full gap-3 sm:gap-4 px-1">
              {DESTINATIONS.slice(
                slideIdx * itemsPerSlide,
                slideIdx * itemsPerSlide + itemsPerSlide,
              ).map((destination) => (
                <div
                  key={destination.title}
                  className="relative flex-1 min-w-0 cursor-pointer group"
                  onClick={() => router.push(destination.route)}
                >
                  {/* Gradient border frame — diagonal asymmetric curve */}
                  <div className="relative rounded-tl-[42px] rounded-tr-[14px] rounded-br-[42px] rounded-bl-[14px] p-[1.5px] bg-gradient-to-br from-purple-400/50 via-white/10 to-transparent dark:from-purple-500/40 dark:via-white/5 shadow-lg shadow-black/10 dark:shadow-black/40 group-hover:shadow-xl group-hover:shadow-purple-900/25 dark:group-hover:shadow-purple-950/60 transition-all duration-300 group-hover:-translate-y-1">
                    <div className="relative h-48 sm:h-56 md:h-64 lg:h-72 rounded-[inherit] overflow-hidden bg-zinc-900">
                      <Image
                        src={destination.image}
                        alt={destination.title}
                        fill
                        className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.08]"
                        sizes={
                          isMobile
                            ? "100vw"
                            : "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                        }
                        priority={slideIdx === 0}
                        unoptimized
                      />
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
                      {/* Text on image */}
                      <div className="absolute bottom-6 left-5 right-16">
                        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-purple-300 mb-1.5">
                          <span className="h-1 w-1 rounded-full bg-purple-400" />
                          {destination.location}
                        </p>
                        <h3 className="text-lg font-bold text-white leading-tight drop-shadow-md line-clamp-1">
                          {destination.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  {/* Arrow badge nested into the deep bottom-right curve */}
                  <div className="absolute bottom-2 right-2 h-11 w-11 rounded-full bg-white/95 dark:bg-zinc-900/95 backdrop-blur-md border border-black/5 dark:border-white/10 flex items-center justify-center shadow-lg shadow-black/15 dark:shadow-black/50 group-hover:bg-purple-600 dark:group-hover:bg-purple-600 group-hover:scale-110 group-hover:rotate-45 transition-all duration-300 ease-out">
                    <ArrowUpRight className="h-4 w-4 text-purple-600 dark:text-purple-400 group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Hidden on mobile when single item */}
        {!isMobile && totalSlides > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous destinations"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-black/5 dark:border-white/10 hover:bg-purple-600 dark:hover:bg-purple-600 rounded-full p-2 shadow-lg shadow-black/10 dark:shadow-black/40 transition-all duration-200 hover:scale-110 group/nav"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-zinc-300 group-hover/nav:text-white transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={next}
              aria-label="Next destinations"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-black/5 dark:border-white/10 hover:bg-purple-600 dark:hover:bg-purple-600 rounded-full p-2 shadow-lg shadow-black/10 dark:shadow-black/40 transition-all duration-200 hover:scale-110 group/nav"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-zinc-300 group-hover/nav:text-white transition-colors"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}

        {/* Mobile Navigation Buttons */}
        {isMobile && totalSlides > 1 && (
          <div className="flex justify-center gap-4 mt-4">
            <button
              onClick={prev}
              aria-label="Previous destination"
              className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-black/5 dark:border-white/10 hover:bg-purple-600 dark:hover:bg-purple-600 rounded-full p-2 shadow-lg shadow-black/10 dark:shadow-black/40 transition-all duration-200 group/nav"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-zinc-300 group-hover/nav:text-white transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={next}
              aria-label="Next destination"
              className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border border-black/5 dark:border-white/10 hover:bg-purple-600 dark:hover:bg-purple-600 rounded-full p-2 shadow-lg shadow-black/10 dark:shadow-black/40 transition-all duration-200 group/nav"
            >
              <svg
                className="w-5 h-5 text-gray-700 dark:text-zinc-300 group-hover/nav:text-white transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Dots Indicator */}
      {totalSlides > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                i === index
                  ? "bg-purple-600 w-6"
                  : "bg-gray-300 dark:bg-zinc-700 hover:bg-gray-400 dark:hover:bg-zinc-600 w-2.5"
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Mobile Swipe Hint */}
      {isMobile && totalSlides > 1 && (
        <div className="text-center mt-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Swipe to explore more destinations
          </p>
        </div>
      )}
    </section>
  );
}

///
// components/top-destinations.tsx
// "use client";
// import { useState } from "react";
// import Image from "next/image";
// import { useRouter } from "next/navigation";

// const DIGITAL_OCEAN_SPACES_CDN =
//   "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

// const galle = {
//   title: "Galle",
//   tag: "Fort city",
//   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/galle.jpg`,
//   route: "/travel-guides/southern",
// };
// const sigiriya = {
//   title: "Sigiriya",
//   tag: "Ancient rock",
//   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/sigiriya.jpg`,
//   route: "/travel-guides/central",
// };
// const ella = {
//   title: "Ella",
//   tag: "Hill country",
//   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/ella.jpg`,
//   route: "/travel-guides/uva",
// };
// const kandy = {
//   title: "Kandy",
//   tag: "Sacred lake",
//   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/kandy.jpg`,
//   route: "/travel-guides/central",
// };
// const colombo = {
//   title: "Colombo",
//   tag: "Capital skyline",
//   image: `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/destinations/colombo.jpg`,
//   route: "/travel-guides/western",
// };

// type Dest = { title: string; tag: string; image: string; route: string };

// function Tile({
//   dest,
//   className = "",
//   priority = false,
//   dimmed = false,
//   onHoverStart,
//   onHoverEnd,
// }: {
//   dest: Dest;
//   className?: string;
//   priority?: boolean;
//   dimmed?: boolean;
//   onHoverStart?: () => void;
//   onHoverEnd?: () => void;
// }) {
//   const router = useRouter();
//   return (
//     <div
//       onClick={() => router.push(dest.route)}
//       onMouseEnter={onHoverStart}
//       onMouseLeave={onHoverEnd}
//       className={`relative overflow-hidden cursor-pointer group border border-white/10 ${className} ${
//         dimmed ? "opacity-50" : "opacity-100"
//       } transition-opacity duration-500 ease-out`}
//     >
//       <Image
//         src={dest.image}
//         alt={dest.title}
//         fill
//         className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
//         sizes="(max-width: 1024px) 100vw, 33vw"
//         priority={priority}
//         unoptimized
//       />

//       {/* Cinematic vignette: darker at bottom + edges, subtle top fade for depth */}
//       <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/20" />
//       <div className="absolute inset-0 bg-gradient-to-r from-black/25 via-transparent to-black/25" />

//       {/* Fine top hairline for a "frame" feel */}
//       <div className="absolute top-0 left-0 right-0 h-px bg-white/20" />

//       <div className="absolute bottom-0 left-0 right-0 p-4">
//         <div className="h-[2px] w-8 bg-purple-400 mb-2 transition-all duration-500 group-hover:w-12" />
//         <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white/60 mb-1">
//           {dest.tag}
//         </p>
//         <h3 className="text-white text-lg sm:text-xl font-bold drop-shadow-lg leading-tight">
//           {dest.title}
//         </h3>
//       </div>
//     </div>
//   );
// }

// export default function TopDestinations() {
//   const router = useRouter();
//   const [hovered, setHovered] = useState<string | null>(null);

//   const tileProps = (dest: Dest, className: string, priority = false) => ({
//     dest,
//     className,
//     priority,
//     dimmed: hovered !== null && hovered !== dest.title,
//     onHoverStart: () => setHovered(dest.title),
//     onHoverEnd: () => setHovered(null),
//   });

//   return (
//     <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
//       <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6">
//         <div>
//           <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-purple-500 mb-1">
//             Sri Lanka
//           </p>
//           <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
//             Trending Destinations
//           </h2>
//         </div>
//         <button
//           onClick={() => router.push("/travel-guides")}
//           className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors mt-4 sm:mt-0 inline-flex items-center"
//         >
//           View All Destinations
//           <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
//           </svg>
//         </button>
//       </div>

//       {/* Desktop / tablet: 3-column crisscross grid, film-strip gaps */}
//       <div className="hidden sm:flex gap-[3px] h-[440px] lg:h-[560px] bg-black">
//         <div className="w-1/3">
//           <Tile {...tileProps(galle, "h-full w-full", true)} />
//         </div>

//         <div className="w-1/3 flex flex-col gap-[3px]">
//           <Tile {...tileProps(sigiriya, "h-3/5 w-full", true)} />
//           <Tile {...tileProps(ella, "h-2/5 w-full")} />
//         </div>

//         <div className="w-1/3 flex flex-col gap-[3px]">
//           <Tile {...tileProps(kandy, "h-2/5 w-full")} />
//           <Tile {...tileProps(colombo, "h-3/5 w-full")} />
//         </div>
//       </div>

//       {/* Mobile: stacked, alternating heights for the same cinematic rhythm */}
//       <div className="flex sm:hidden flex-col gap-[3px] bg-black">
//         {[galle, sigiriya, kandy, ella, colombo].map((d, i) => (
//           <Tile
//             key={d.title}
//             {...tileProps(d, i % 2 === 0 ? "h-56 w-full" : "h-40 w-full", i === 0)}
//           />
//         ))}
//       </div>
//     </section>
//   );
// }
